import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// icons
import CheckedIcon from "@surgia/ui/icons/checkbox/checked.svg";
import UncheckedIcon from "@surgia/ui/icons/checkbox/unchecked.svg";
import editProductImage from "@surgia/ui/icons/backgrounds/editProduct.svg";

// queries
import { tenantQueries } from "@surgia/api/src/queryStore/shared/tenant";
import { productQueries } from "@surgia/api/src/queryStore/admin/product";
import { lookupQueries } from "@surgia/api/src/queryStore/shared/lookup";
import { tenantQueries as adminTenantQueries } from "@surgia/api/src/queryStore/admin/tenant";
//components
import ProductCardFinal from "../../containers/ProductCardFinal/index";
import { ProductConfiguration } from "../../containers/ProductCardFinal/types";
import { toast } from "sonner";
import { useAssignProduct } from "../../hooks/useAssignProduct";
import { useAssignParentProduct } from "../../hooks/useAssignParentProduct";
import ProductDetailsSkeleton from "./Skeleton";
import ProductNotFound from "./NotFound";
import Header from "./Header";
import config from "@surgia/config";

interface ProductDetailsProps {}

function ProductDetails({}: ProductDetailsProps) {
  const { id: tenantId, tenantName, productId } = useParams();
  const location = useLocation();
  
  // Try to get currency from navigation state first (if navigated from TenantDetails)
  const currencyFromState = location.state?.currencyCode;

  const [selectedVariants, setSelectedVariants] = useState<Set<string>>(
    new Set()
  );

  // State for ProductCardFinal functionality (from customerView.tsx)
  const queryClient = useQueryClient();
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(
    new Set()
  );
  const [expandedVariants, setExpandedVariants] = useState<Set<string>>(
    new Set()
  );
  // productConfigs removed - now using raw API data directly

  // Move the useAssignParentProduct hook to the top level
  const { assignParentProducts } = useAssignParentProduct({
    tenantId: tenantId!,
  });

  // Fetch tenant data to get storefront_id
  const { data: tenantResponse } = useQuery({
    ...adminTenantQueries.queries.tenants({
      status: "ACTIVE",
      id: tenantId ? +tenantId : undefined,
    }),
  });

  // Get storefront_id from tenant response
  const storefront_id = tenantResponse?.data?.[0]?.storefront_id;

  // Fetch storefront currency - will use cache if already fetched in TenantDetails
  const { data: storefrontCurrencyData } = useQuery({
    ...lookupQueries.queries.storefrontCurrency({
      storefront_id: Number(storefront_id) || 0,
      is_base_currency: true,
    }),
    enabled: !!storefront_id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Get currency code - prefer from state, then cache
  const currencyCode = 
    currencyFromState || 
    storefrontCurrencyData?.[0]?.currency?.code;

  // Fetch tenant products data
  const {
    data: tenantProductsResponse,
    isLoading,
    isSuccess,
  } = useQuery({
    ...tenantQueries.queries.tenantProducts({
      tenant_id: +tenantId!,
      component_id: config.componentId,
      page: 0,
      size: 10,
      product_id: productId!,
      currency_code: currencyCode,
      // currently_assigned: true,
    }),
  });
  const { toggleProductAssignment, isAssigning } = useAssignProduct({
    tenantId: tenantId || "",
    onSuccess: () => {
      // Refetch the product variations to get updated assignment states
      queryClient.invalidateQueries({
        predicate: (query) => {
          const keyString = JSON.stringify(query.queryKey);
          return (
            keyString.includes("product") || keyString.includes("variations")
          );
        },
      });
    },
  });
  // Find the specific product by matching the decoded name
  const currentProduct = tenantProductsResponse?.data?.[0]; // Get the first result since we're filtering by name

  // Use the new hook for product assignment

  // Mutations from customerView.tsx - removed updateProductMutation as it's now in ProductCardFinal

  // Mutation for updating customer view
  const updateCustomerViewMutation = useMutation({
    mutationFn: async ({
      productId,
      customerView,
    }: {
      productId: string;
      customerView: boolean;
    }) => {
      return productQueries.mutations
        .updateTenantProductCustomerView(productId)
        .mutationFn({ customerView });
    },
    onSuccess: () => {
      toast.success("Visibility updated successfully!");
      queryClient.invalidateQueries({
        predicate: (query) => {
          const keyString = JSON.stringify(query.queryKey);
          return keyString.includes("variations");
        },
      });
    },
    onError: (error) => {
      toast.error("Failed to update visibility. Please try again.");
      console.error("Error updating visibility:", error);
    },
  });

  // Helper functions from customerView.tsx
  const toggleProductExpansion = (productId: string) => {
    setExpandedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const toggleVariantExpansion = (variantId: string) => {
    setExpandedVariants((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(variantId)) {
        newSet.delete(variantId);
      } else {
        newSet.add(variantId);
      }
      return newSet;
    });
  };

  // Fetch variants for side panel - keep this here as it's used by the side panel
  const { data: productVariationsResponse, isLoading: isLoadingVariations } =
    useQuery({
      ...productQueries.queries.productVariations(
        currentProduct?.id?.toString() || "",
        tenantId || ""
      ),
      enabled: isSuccess && !!currentProduct?.product_id,
    });
  // Initialize product configs when currentProduct changes
  useEffect(() => {
    if (currentProduct) {
      const initialProductConfig: Record<string, ProductConfiguration> = {};
      const initialSelectedVariants = new Set<string>();

      // Find the product in tenantProductsDataRef.current that matches the currentProduct
      const originalProduct = tenantProductsResponse?.data?.find(
        (p: any) => p?.product_id?.toString() === currentProduct?.id.toString()
      );

      if (originalProduct) {
        // This is a parent product
        initialProductConfig[originalProduct?.id.toString()] = {
          id: originalProduct?.id.toString(),
          name: originalProduct.catalog_info.name,
          price: originalProduct.catalog_info?.price || null,
          minQuantity: originalProduct.minimum_quantity || null,
          maxQuantity: originalProduct.maximum_quantity || null,
          customerProductId: originalProduct.tenant_product_sku || "",
          customerProductName: originalProduct.tenant_product_name || "",
          isParent: true,
          tenantProductId: originalProduct?.id.toString(),
          isVisible: true,
          hasInitialValues: true,
          hasChanges: false,
          tenantInfo: null, // Parent products don't need tenantInfo
        };
      }

      // Initialize selectedVariants based on currently_assigned status from productVariationsResponse
      if (productVariationsResponse?.length > 0) {
        productVariationsResponse.forEach((variant: any) => {
          // Skip the parent product itself
          if (
            variant.product_id.toString() ===
            currentProduct?.product_id?.toString()
          ) {
            return;
          }

          // Add to selectedVariants if currently_assigned is true
          if (variant.currently_assigned) {
            initialSelectedVariants.add(variant.id.toString());
          }
        });
      }

      setSelectedVariants(initialSelectedVariants);
    }
  }, [currentProduct, productVariationsResponse]); // Add productVariationsResponse as dependency

  // Loading state
  if (isLoading) {
    return <ProductDetailsSkeleton />;
  }

  // Product not found
  if (!currentProduct) {
    return <ProductNotFound />;
  }
  // Enhanced handleVariantToggle function
  const handleVariantToggle = async (variantId: string) => {
    try {
      // Get the current assignment state
      // const isCurrentlyAssigned = selectedVariants.has(variantId);

      // Optimistically update the UI
      setSelectedVariants((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(variantId)) {
          newSet.delete(variantId);
        } else {
          newSet.add(variantId);
        }
        return newSet;
      });

      // Call the API to toggle assignment
      await toggleProductAssignment(
        variantId,
        parseInt(currentProduct?.id?.toString() || productId || "0")
      );

      // The success callback will handle refetching data
    } catch (error) {
      // Revert the optimistic update on error
      setSelectedVariants((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(variantId)) {
          newSet.delete(variantId);
        } else {
          newSet.add(variantId);
        }
        return newSet;
      });

      console.error("Error toggling variant assignment:", error);
    }
  };

  const handleAssignParentProduct = async (productId: string) => {
    try {
      await assignParentProducts({
        parentProductIds: [parseInt(productId)],
      });
    } catch (error) {
      console.error("Error assigning parent product:", error);
    }
  };
  return (
    <div className="flex flex-col items-start p-0 relative w-full min-h-screen bg-white">
      {/* Page Header */}
      <Header
        productName={currentProduct?.catalog_info.name}
        tenantName={tenantName}
      />

      {/* Main Content */}
      <div className="flex flex-col-reverse md:flex-row w-full flex-1 p-4 sm:p-6 gap-4 sm:gap-6">
        {/* Side Panel */}
        <div className="w-full md:w-80 lg:w-96 flex-shrink-0">
          <div className="bg-white border border-gray-200 shadow-sm rounded-xl overflow-hidden">
            {/* Product Image */}
            <img
              src={
                currentProduct?.catalog_info?.product_images?.[0]?.image_path ??
                editProductImage
              }
              alt="Product Image"
              className="w-full h-48 sm:h-56 object-contain"
            />

            {/* Product Names */}
            <div className="px-4 sm:px-6 py-4">
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
                  {currentProduct?.catalog_info.name || "Product common name"}
                </h3>
                <p className="text-xs sm:text-sm text-gray-600">
                  {currentProduct?.tenant_product_name ||
                    "Product customer name"}
                </p>
              </div>
              <h4 className="font-medium text-gray-900 mb-3 text-sm sm:text-base">
                Variants
              </h4>

              {/* Variants List */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {isLoadingVariations ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-start gap-3 p-2">
                        <div className="w-4 h-4 sm:w-5 sm:h-5 mt-0.5 flex-shrink-0 bg-gray-200 rounded animate-pulse" />
                        <div className="flex-1 space-y-1">
                          <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4" />
                          <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : productVariationsResponse?.length > 0 ? (
                  productVariationsResponse
                    .filter(
                      (product: any) =>
                        // Skip the parent product itself
                        product.product_id.toString() !==
                        currentProduct?.product_id?.toString()
                    )
                    .map((product: any) => {
                      const featureDisplay =
                        product?.catalog_info?.feature_values
                          ?.map(
                            (featureValue: any) =>
                              featureValue.featureVariant.variant_name
                          )
                          .join(", ") || "";

                      const variantId = product.product_id.toString();

                      return (
                        <label
                          key={variantId}
                          className="flex items-start gap-3 cursor-pointer p-2  rounded"
                        >
                          <div className="relative flex-shrink-0 mt-0.5">
                            <input
                              type="checkbox"
                              checked={selectedVariants.has(variantId)}
                              onChange={() => handleVariantToggle(variantId)}
                              disabled={isAssigning}
                              className="sr-only"
                            />
                            <img
                              src={
                                product?.currently_assigned
                                  ? CheckedIcon
                                  : UncheckedIcon
                              }
                              alt={
                                selectedVariants.has(variantId)
                                  ? "Checked"
                                  : "Unchecked"
                              }
                              className={`w-4 h-4 sm:w-5 sm:h-5 ${
                                isAssigning ? "opacity-50" : ""
                              }`}
                            />
                          </div>
                          <span
                            className={`text-xs sm:text-sm text-gray-700 leading-relaxed ${
                              isAssigning ? "opacity-50" : ""
                            }`}
                          >
                            {featureDisplay}
                          </span>
                        </label>
                      );
                    })
                ) : (
                  <div className="text-sm text-gray-500 italic">
                    No variants available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1">
          {(() => {
            // Since we filter by productId, we only get one product
            const parentProduct = tenantProductsResponse?.data?.[0];

            return (
              <ProductCardFinal
                key={parentProduct?.id}
                productId={parentProduct?.id?.toString()}
                catalogProductId={
                  parentProduct?.product_id?.toString() ||
                  parentProduct?.catalog_info?.id?.toString()
                }
                productName={parentProduct?.catalog_info?.name}
                productImage={
                  parentProduct?.catalog_info?.product_images?.[0]?.image_path
                }
                initialPrice={
                  parentProduct?.tenant_product_prices?.[0]?.price ||
                  parentProduct?.catalog_info?.price
                }
                initialMinQuantity={parentProduct?.minimum_quantity!}
                initialMaxQuantity={parentProduct?.maximum_quantity!}
                initialCustomerProductId={parentProduct?.tenant_product_sku!}
                initialCustomerProductName={parentProduct?.tenant_product_name!}
                expandedProducts={expandedProducts}
                expandedVariants={expandedVariants}
                onToggleProductExpansion={toggleProductExpansion}
                onToggleVariantExpansion={toggleVariantExpansion}
                isUpdatingProduct={false}
                updatingProductId={null}
                updateCustomerViewMutation={updateCustomerViewMutation}
                isProductDetailsPage={true}
                onAssignParentProduct={handleAssignParentProduct}
                tenantId={tenantId || ""}
                currencyCode={currencyCode} // Pass currency code
              />
            );
          })()}
        </div>
      </div>
    </div>
  );
}

export default ProductDetails;
